import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  getOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  testIMAPConnection,
  testOrganizationIMAP,
  testSMTPConnection,
  testOrganizationSMTP,
  type Organization,
  type CreateOrganizationData,
  type UpdateOrganizationData,
  type IMAPTestResult,
  type SMTPTestResult
} from '@/services/organizations.service';
import { useTranslation } from 'react-i18next';

// Organizations Query
export const useOrganizations = () => {
  const { t } = useTranslation();
  return useQuery({
    queryKey: ['organizations'],
    queryFn: getOrganizations,
  });
};

// Create Organization Mutation
export const useCreateOrganization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: createOrganization,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: 'Organisation erfolgreich erstellt',
        description: `Organisation ${data.name} wurde aktiviert.`
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:org.createFailed', 'Organisation konnte nicht erstellt werden');
      toast({
        title: 'Fehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};

// Update Organization Mutation
export const useUpdateOrganization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationData }) =>
      updateOrganization(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: 'Organisation aktualisiert',
        description: `Organisation ${data.name} wurde erfolgreich aktualisiert.`
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:org.updateFailed', 'Organisation konnte nicht aktualisiert werden');
      toast({
        title: 'Fehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};

// Delete Organization Mutation
export const useDeleteOrganization = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: deleteOrganization,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      toast({
        title: 'Organisation gelöscht',
        description: `Organisation ${data.name} wurde erfolgreich gelöscht.`
      });
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:org.deleteFailed', 'Organisation konnte nicht gelöscht werden');
      toast({
        title: 'Fehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};

// IMAP Test Mutations
export const useTestIMAPConnection = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  return useMutation({
    mutationFn: testIMAPConnection,
    onSuccess: (data: IMAPTestResult) => {
      if (data.success) {
        toast({
          title: 'IMAP-Verbindung erfolgreich',
          description: `Erfolgreich mit Server ${data.details.host} verbunden. ${data.emailCount || 0} E-Mails gefunden.`
        });
      } else {
        toast({
          title: 'IMAP-Verbindung fehlgeschlagen',
          description: data.error || t('errors:generic.unknown', 'Unbekannter Fehler aufgetreten'),
          variant: 'destructive'
        });
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:conn.imapFailed', 'IMAP-Test fehlgeschlagen');
      toast({
        title: 'IMAP-Testfehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};

export const useTestOrganizationIMAP = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  return useMutation({
    mutationFn: testOrganizationIMAP,
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: 'Organisations-IMAP-Test erfolgreich',
          description: `IMAP-Verbindung für ${data.organizationName} funktioniert. ${data.emailCount || 0} E-Mails gefunden.`
        });
      } else {
        toast({
          title: t('errors:conn.orgImapFailed', 'Organisations-IMAP-Test fehlgeschlagen'),
          description: `${data.organizationName}: ${data.error || t('errors:generic.unknown', 'Unbekannter Fehler aufgetreten')}`,
          variant: 'destructive'
        });
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:conn.orgImapFailed', 'Organisations-IMAP-Test fehlgeschlagen');
      toast({
        title: 'IMAP-Testfehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};

// SMTP Test Mutations
export const useTestSMTPConnection = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  return useMutation({
    mutationFn: testSMTPConnection,
    onSuccess: (data: SMTPTestResult) => {
      if (data.success) {
        toast({
          title: 'SMTP-Verbindung erfolgreich',
          description: `Erfolgreich mit Server ${data.details?.host} verbunden.`
        });
      } else {
        toast({
          title: 'SMTP-Verbindung fehlgeschlagen',
          description: data.error || t('errors:generic.unknown', 'Unbekannter Fehler aufgetreten'),
          variant: 'destructive'
        });
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:conn.smtpFailed', 'SMTP-Test fehlgeschlagen');
      toast({
        title: 'SMTP-Testfehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};

export const useTestOrganizationSMTP = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  return useMutation({
    mutationFn: testOrganizationSMTP,
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: 'Organisations-SMTP-Test erfolgreich',
          description: `SMTP-Verbindung für ${data.organizationName} funktioniert.`
        });
      } else {
        toast({
          title: t('errors:conn.orgSmtpFailed', 'Organisations-SMTP-Test fehlgeschlagen'),
          description: `${data.organizationName}: ${data.error || t('errors:generic.unknown', 'Unbekannter Fehler aufgetreten')}`,
          variant: 'destructive'
        });
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('errors:conn.orgSmtpFailed', 'Organisations-SMTP-Test fehlgeschlagen');
      toast({
        title: 'SMTP-Testfehler',
        description: message,
        variant: 'destructive'
      });
    },
  });
};